import React, { useState, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  useTheme,
  alpha
} from '@mui/material'
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
  CheckCircle as SuccessIcon,
  Delete as DeleteIcon
} from '@mui/icons-material'
import apiClient from '../../services/api'
import toast from 'react-hot-toast'

interface SimpleDocumentUploadDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  patientId: string
  patientName: string
}

const DOCUMENT_TYPES = [
  { value: 'referral', label: 'Bilet de Trimitere' },
  { value: 'medical_letter', label: 'Scrisoare Medicală' },
  { value: 'external_result', label: 'Rezultat Analize' },
  { value: 'external_report', label: 'Raport Imagistic Extern' },
  { value: 'consent', label: 'Consimțământ' },
  { value: 'generic', label: 'Alt Document' },
]

/**
 * SimpleDocumentUploadDialog - Dialog simplificat pentru upload document
 * 
 * Features:
 * - Drag & Drop intuitiv
 * - Doar 2 câmpuri: Tip Document + Denumire (opțional)
 * - Progress vizual pentru upload
 * - Suport dark mode
 */
const SimpleDocumentUploadDialog: React.FC<SimpleDocumentUploadDialogProps> = ({
  open,
  onClose,
  onSuccess,
  patientId,
  patientName
}) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const { designSystem } = theme
  const colors = isDark ? designSystem.colors.dark : designSystem.colors.light

  const [file, setFile] = useState<File | null>(null)
  const [kind, setKind] = useState<string>('generic')
  const [customName, setCustomName] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [dragActive, setDragActive] = useState(false)
  const [uploadSuccess, setUploadSuccess] = useState(false)

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0])
    }
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0])
    }
  }

  const handleFile = (selectedFile: File) => {
    // Validare dimensiune (max 25MB)
    if (selectedFile.size > 25 * 1024 * 1024) {
      toast.error('Fișierul este prea mare (max 25MB)')
      return
    }
    setFile(selectedFile)
    setUploadSuccess(false)
    
    // Auto-detect tip bazat pe nume fișier
    const lowerName = selectedFile.name.toLowerCase()
    if (lowerName.includes('bilet') || lowerName.includes('trimitere')) {
      setKind('referral')
    } else if (lowerName.includes('analiz') || lowerName.includes('rezultat')) {
      setKind('external_result')
    } else if (lowerName.includes('rmn') || lowerName.includes('ct') || lowerName.includes('raport')) {
      setKind('external_report')
    } else if (lowerName.includes('consimtamant') || lowerName.includes('acord')) {
      setKind('consent')
    }
  }

  const handleSubmit = async () => {
    if (!file) {
      toast.error('Selectați un fișier')
      return
    }

    setUploading(true)
    setUploadProgress(0)
    
    const formData = new FormData()
    formData.append('file', file)
    formData.append('kind', kind)
    formData.append('patient_id', patientId)
    
    // Folosește denumirea custom dacă e completată, altfel păstrează numele original
    if (customName.trim()) {
      formData.append('custom_name', customName.trim())
    }

    try {
      await apiClient.post('/documents/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = progressEvent.total 
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0
          setUploadProgress(progress)
        }
      })
      
      setUploadSuccess(true)
      setUploadProgress(100)
      
      toast.success('Document încărcat cu succes!')
      
      // Așteaptă puțin pentru animație, apoi închide
      setTimeout(() => {
        onSuccess()
        resetForm()
      }, 800)
      
    } catch (error: any) {
      console.error('Upload error:', error)
      toast.error(error.response?.data?.detail || 'Eroare la încărcare')
      setUploadProgress(0)
    } finally {
      setUploading(false)
    }
  }

  const resetForm = () => {
    setFile(null)
    setKind('generic')
    setCustomName('')
    setUploadProgress(0)
    setUploadSuccess(false)
  }

  const handleClose = () => {
    if (!uploading) {
      resetForm()
      onClose()
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`
  }

  return (
    <Dialog 
      open={open} 
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          handleClose();
        }
      }}
      disableEscapeKeyDown
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          '& .MuiInputLabel-root': {
            color: 'text.secondary',
            fontSize: '0.85rem',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: 'primary.main',
          },
          '& .MuiOutlinedInput-root': {
            fontSize: '0.875rem',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'divider',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
            },
          },
          '& .MuiSelect-select': {
            color: 'text.primary',
          },
          '& .MuiMenuItem-root': {
            fontSize: '0.85rem',
          },
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        py: 1.5,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: 'text.primary' }}>
            Încarcă Document
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {patientName}
          </Typography>
        </Box>
        {!uploading && (
          <IconButton 
            onClick={handleClose} 
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {/* Drag & Drop Zone */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 2,
            textAlign: 'center',
            cursor: uploading ? 'default' : 'pointer',
            backgroundColor: dragActive 
              ? alpha(colors.accent.primary, 0.08)
              : file 
                ? alpha(colors.accent.primary, 0.04)
                : 'action.hover',
            border: '2px dashed',
            borderColor: dragActive 
              ? 'primary.main' 
              : file 
                ? alpha(colors.accent.primary, 0.5)
                : 'divider',
            borderRadius: 1,
            transition: 'all 0.2s ease',
            '&:hover': !uploading ? {
              borderColor: 'primary.main',
              backgroundColor: alpha(colors.accent.primary, 0.04),
            } : {}
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !uploading && document.getElementById('simple-doc-upload')?.click()}
        >
          <input
            type="file"
            id="simple-doc-upload"
            style={{ display: 'none' }}
            onChange={handleChange}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.dicom,.dcm"
            disabled={uploading}
          />
          
          {file ? (
            <Box>
              {uploadSuccess ? (
                <SuccessIcon sx={{ fontSize: 36, color: designSystem.colors.status.success, mb: 0.5 }} />
              ) : (
                <FileIcon sx={{ fontSize: 36, color: 'primary.main', mb: 0.5 }} />
              )}
              <Typography 
                variant="body2" 
                noWrap 
                sx={{ 
                  fontWeight: 600, 
                  color: 'text.primary',
                  maxWidth: '100%',
                  fontSize: '0.85rem'
                }}
              >
                {file.name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {formatFileSize(file.size)}
              </Typography>
              
              {/* Progress Bar */}
              {uploading && (
                <Box sx={{ mt: 1.5, px: 2 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={uploadProgress}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: alpha(colors.accent.primary, 0.2),
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        backgroundColor: uploadSuccess 
                          ? designSystem.colors.status.success 
                          : colors.accent.primary,
                      }
                    }}
                  />
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary', 
                      mt: 0.5, 
                      display: 'block',
                      fontSize: '0.7rem'
                    }}
                  >
                    {uploadSuccess ? 'Încărcat cu succes!' : `Se încarcă... ${uploadProgress}%`}
                  </Typography>
                </Box>
              )}
              
              {!uploading && !uploadSuccess && (
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon sx={{ fontSize: '0.9rem' }} />}
                  onClick={(e) => {
                    e.stopPropagation()
                    setFile(null)
                  }}
                  sx={{ mt: 1, fontSize: '0.75rem' }}
                >
                  Elimină
                </Button>
              )}
            </Box>
          ) : (
            <Box>
              <CloudUploadIcon sx={{ fontSize: 36, color: 'text.secondary', mb: 0.5 }} />
              <Typography 
                variant="body2" 
                sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.85rem' }}
              >
                Trageți fișierul aici
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                sau click pentru a selecta
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.disabled', 
                  mt: 1, 
                  display: 'block',
                  fontSize: '0.7rem'
                }}
              >
                PDF, Imagini, DOCX • Max 25MB
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Form Fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Tip Document */}
          <FormControl fullWidth size="small">
            <InputLabel>Tip Document</InputLabel>
            <Select
              value={kind}
              label="Tip Document"
              onChange={(e) => setKind(e.target.value)}
              disabled={uploading}
            >
              {DOCUMENT_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value} sx={{ fontSize: '0.85rem', py: 0.5 }}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Denumire Personalizată (Opțional) */}
          <TextField
            fullWidth
            size="small"
            label="Denumire (opțional)"
            placeholder={file ? file.name : 'Se păstrează numele original'}
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            disabled={uploading}
            helperText="Lasă gol pentru a păstra numele fișierului"
            FormHelperTextProps={{ sx: { fontSize: '0.7rem' } }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        px: 2, 
        py: 1.5, 
        gap: 1,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}>
        <Button 
          onClick={handleClose} 
          disabled={uploading}
          size="small"
          sx={{ 
            fontSize: '0.8rem',
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary'
            }
          }}
        >
          Anulează
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!file || uploading || uploadSuccess}
          startIcon={uploading ? null : <CloudUploadIcon sx={{ fontSize: '1rem' }} />}
          size="small"
          sx={{ fontSize: '0.8rem' }}
        >
          {uploading ? 'Se încarcă...' : 'Încarcă'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SimpleDocumentUploadDialog
